// src/components/citizen/CitizenProfile.jsx
import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import {
  Segment,
  Grid,
  Header,
  Form,
  Icon,
  Container,
  Divider,
  Label,
  Checkbox,
  Message,
  MessageHeader,
  Loader,
  Button
} from 'semantic-ui-react';
import Configuration from '../../config/Configuration.js';
import { authHeaderPatch, authHeader, authHeaderSet } from '../../helpers/auth-header.js';
import moment from 'moment';
import { withTranslation } from 'react-i18next';
import i18n from "../../i18n.js";

class CitizenProfile extends Component {
  constructor() {
    super();
    this.config = new Configuration();
    this.postalInputRef = React.createRef();

    this.state = {
      identifier: '',
      familyName: '',
      givenName: '',
      phone: '',
      email: '',
      gender: '',
      birthdate: '',
      addressCountry: '',
      addressPostalCode: '',
      patientCreationError: false,
      patientCreated: false,
      errorMsg: '',
      readOnlyMode: true,

      // Solid
      isStoreSolid: false,
      saveError: '',

      // WeAre/RDF (alleen postcode tonen)
      rdfPostalCode: '',
      postalMismatch: false,
      loadingWeArePostal: false,
      loadWeAreError: '',
      noActiveAccessGrant: false,

      // Nieuw: waarschuwing wanneer postcode ontbreekt
      needsPostal: false,
    };
  }

  componentDidMount() {
    const { activeUser } = this.props;

    // Als er (nog) geen actieve gebruiker is, sla de call over.
    if (!activeUser || !activeUser.uuid) {
      console.warn('CitizenProfile: geen activeUser beschikbaar, sla profile-opvraging over.');
      return;
    }

    this.retrieveProfile(activeUser.uuid);
  }

  handleChange = (e, { name, value }) => {
    this.setState(
      { [name]: value },
      () => {
        if (name === 'addressPostalCode') this.updateNeedsPostal();
      }
    );
  };

  // Bepaalt of we de banner moeten tonen
  updateNeedsPostal = () => {
    const needsPostal = !(this.state.addressPostalCode || '').toString().trim();
    this.setState({ needsPostal });
  };

  focusPostalAndEdit = () => {
    this.setState({ readOnlyMode: false }, () => {
      const el = this.postalInputRef && this.postalInputRef.current;
      if (el && typeof el.focus === 'function') {
        el.focus();
      }
    });
  };

  retrieveProfile(userUuid) {
    return fetch(`${this.config.PATIENTS_URL}/${userUuid}`, {
      method: 'GET',
      headers: authHeader()
    })
      .then(response => {
        if (!response.ok) this.handleResponseError(response);
        return response.json();
      })
      .then(async citizen => {
        this.setState({
          identifier: citizen.identifier,
          familyName: citizen.familyName,
          givenName: citizen.givenName,
          phone: citizen.phone,
          email: citizen.email,
          gender: citizen.gender,
          birthdate: moment(citizen.birthdate).format('YYYY-MM-DD'),
          address: citizen.address,
          isStoreSolid: !!citizen.isStoreSolid
        });
        if (citizen.address) this.retrieveAddress(citizen.address.uuid);

        if (citizen.isStoreSolid) {
          const hasGrant = await this.checkActiveAccessGrant();
          if (hasGrant) {
            this.loadWeArePostal();
          } else {
            this.setState({ noActiveAccessGrant: true });
          }
        }
      })
      .catch(error => this.handleError(error));
  }

  retrieveAddress = (addressUuid) => {
    return fetch(`${this.config.ADDRESSES_URL}/${addressUuid}`, {
      method: 'GET',
      headers: authHeader()
    })
      .then(response => {
        if (!response.ok) this.handleResponseError(response);
        return response.json();
      })
      .then(address => {
        this.setState({
          addressCountry: address.country,
          addressPostalCode: address.postalCode,
        }, () => {
          this.updateNeedsPostal();
          if (this.state.rdfPostalCode) {
            this.comparePostalCodes(this.state.rdfPostalCode);
          }
        });
      })
      .catch(error => this.handleError(error));
  }

  // ---- Access Grant helpers ----
  // ---- Access Grant helpers ----
  checkActiveAccessGrant = async () => {
    const url = this.config.WELLDATA_ACCESS_GRANT_CALL;
    try {
      const resp = await fetch(url, {
        method: "GET",
        headers: { ...authHeader() },
        cache: "no-store",
      });

      // 401/404/204 = "geen grant" (of niet ingelogd)
      if ([401, 404, 204].includes(resp.status)) return false;

      // 406 = meestal Accept mismatch → behandel als "niet kunnen bepalen"
      if (resp.status === 406) {
        console.warn("[CitizenProfile] access-grant 406 (Accept mismatch?)", url);
        return false;
      }

      if (!resp.ok) return false;

      const data = await resp.json().catch(() => ({}));
      console.log("[CitizenProfile] access-grant data:", data);

      // Tolerant: backend kan verschillende vormen teruggeven
      const active =
        data?.active === true ||
        data?.isActive === true ||
        data?.grant?.active === true ||
        data?.accessGrant?.active === true ||
        data?.status === "ACTIVE" ||
        data?.state === "ACTIVE";

      return !!active;
    } catch (e) {
      console.warn("[CitizenProfile] access-grant check failed:", e);
      return false;
    }
  };

  startAccessRequest = async () => {
    this.setState({ loadWeAreError: '' });
    try {
      const r = await fetch(this.config.WELLDATA_ACCESS_REQUEST_CALL, {
        method: 'GET',
        headers: authHeader()
      });
      if (!r.ok) throw new Error('Access request init failed');
      const data = await r.json();
      const launchUrl = data['grant_url'] || data.url;
      if (!launchUrl) throw new Error('Geen Access Grant URL in response');
      const w = window.open(launchUrl, 'weare-grant', 'noopener');
      window.addEventListener("focus", async () => {
        const hasGrant = await this.checkActiveAccessGrant();
        if (hasGrant) this.loadWeArePostal();
      }, { once: true });
      if (!w) throw new Error('Popup/tab werd geblokkeerd door de browser');
    } catch (e) {
      this.setState({ loadWeAreError: e.message || 'Kon access-grant niet starten' });
    }
  };

  // --- WeAre/RDF helpers ---
  extractPostalFromRdf = (rdfText) => {
    if (!rdfText || typeof rdfText !== 'string') return '';

    const re = /<http:\/\/hl7\.org\/fhir\/postalCode>\s*\[\s*<http:\/\/hl7\.org\/fhir\/v>\s*"([^"]+)"/is;
    const m = rdfText.match(re);

    return m ? (m[1] || '').trim() : '';
  };

  comparePostalCodes = (rdfPostal) => {
    const zipPostal = (this.state.addressPostalCode || '').toString().trim();
    const solidPostal = (rdfPostal || '').toString().trim();
    const postalMismatch = !!solidPostal && (!zipPostal || solidPostal !== zipPostal);
    this.setState({
      rdfPostalCode: solidPostal,
      postalMismatch
    });
  };

  getSolidUrlFromItem = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    return item.solidUrl || item.url || item.id || '';
  };

  // Haal de eerste Patient-asset op en lees meteen de postcode uit RDF
  loadWeArePostal = async () => {
    this.setState({ loadingWeArePostal: true, loadWeAreError: '', noActiveAccessGrant: false });
    try {
      const listResp = await fetch(
        `${this.config.WELLDATA_LIST_SOLID_ASSETS}?folderName=patient/`,
        { method: 'GET', headers: authHeader() }
      );
      if (listResp.status === 401) {
        this.setState({ noActiveAccessGrant: true, loadingWeArePostal: false });
        return;
      }
      if (!listResp.ok) throw new Error(`HTTP ${listResp.status}`);
      const listJson = await listResp.json();

      const items = Array.isArray(listJson)
        ? listJson
        : (
          listJson?.identifierList ||
          listJson?.items ||
          listJson?._embedded?.items ||
          []
        );

      if (!items || items.length === 0) {
        throw new Error('Geen Patient data gevonden in je Solid pod.');
      }

      let foundPostal = '';
      let lastErr = null;

      for (const it of items) {
        const solidUrl = this.getSolidUrlFromItem(it);
        if (!solidUrl) continue;

        try {
          const readResp = await fetch(
            `${this.config.WELLDATA_READ_SOLID_URL}?solidUrl=${encodeURIComponent(solidUrl)}`,
            { method: 'GET', headers: authHeader() }
          );
          if (readResp.status === 401) {
            this.setState({ noActiveAccessGrant: true, loadingWeArePostal: false });
            return;
          }
          if (!readResp.ok) throw new Error(`HTTP ${readResp.status}`);

          const readJson = await readResp.json();
          const rdfText = readJson?.readRdfString || '';
          const maybe = this.extractPostalFromRdf(rdfText);
          if (maybe) {
            foundPostal = maybe;
            break;
          }
        } catch (e) {
          lastErr = e;
        }
      }

      if (!foundPostal) {
        throw new Error(lastErr?.message || 'Geen postcode in RDF gevonden.');
      }

      this.comparePostalCodes(foundPostal);
      this.setState({ loadingWeArePostal: false });
    } catch (err) {
      this.setState({
        loadWeAreError: err.message || 'Kon WeAre-postcode niet ophalen',
        loadingWeArePostal: false
      });
    }
  };

  // Neem WeAre-postcode over in het formulier
  applyPostalFromWeAre = () => {
    const { rdfPostalCode } = this.state;
    if (!rdfPostalCode) return;
    this.setState({
      addressPostalCode: rdfPostalCode,
      readOnlyMode: false,
      postalMismatch: false
    }, this.updateNeedsPostal);
  };

  // --- Profiel opslaan ---
  updateProfile = () => {
    if (this.state.address) {
      this.updateAddress();
    } else {
      this.createAddress();
    }
  }

  updateCitizenProfile = (addressId) => {
    return fetch(`${this.config.PATIENTS_URL}/${this.props.activeUser.uuid}`, {
      method: 'PUT',
      headers: authHeaderPatch(),
      body: JSON.stringify({
        name: `${this.state.givenName} ${this.state.familyName}`,
        identifier: this.state.identifier,
        familyName: this.state.familyName,
        givenName: this.state.givenName,
        phone: this.state.phone,
        email: this.state.email,
        gender: this.state.gender,
        birthdate: this.state.birthdate,
        address: addressId,
        isStoreSolid: this.state.isStoreSolid
      })
    })
      .then(response => {
        if (!response.ok) this.handleResponseError(response);
        this.setState({ readOnlyMode: true }, this.updateNeedsPostal);
        return response.json();
      })
      .catch(error => this.handleError(error));
  }

  updateAddress = () => {
    return fetch(`${this.config.ADDRESSES_URL}/${this.state.address.uuid}`, {
      method: 'PUT',
      headers: authHeaderPatch(),
      body: JSON.stringify({
        postalCode: this.state.addressPostalCode,
        country: this.state.addressCountry
      })
    })
      .then(response => {
        if (!response.ok) this.handleResponseError(response);
        this.setState({ readOnlyMode: true }, this.updateNeedsPostal);
        return response.json();
      })
      .then(() => this.updateCitizenProfile(this.state.address.uuid))
      .catch(error => this.handleError(error));
  }

  createAddress = () => {
    return fetch(this.config.ADDRESSES_URL, {
      method: 'POST',
      headers: authHeaderPatch(),
      body: JSON.stringify({
        postalCode: this.state.addressPostalCode,
        country: this.state.addressCountry
      })
    })
      .then(response => {
        if (!response.ok) this.handleResponseError(response);
        this.setState({ readOnlyMode: true }, this.updateNeedsPostal);
        return response.json();
      })
      .then(json => {
        const addressId = json.id;
        this.updateCitizenProfile(addressId);
      })
      .catch(error => this.handleError(error));
  }

  // --- Solid toggle ---
  toggleIsStoreSolid = async (_, data) => {
    const checked = data.checked;
    this.setState({ isStoreSolid: checked, saveError: '' });
    try {
      const resp = await fetch(
        `${this.config.PATIENTS_URL}/${this.props.activeUser.uuid}`,
        {
          method: 'PATCH',
          headers: authHeaderPatch(),
          body: JSON.stringify({ isStoreSolid: checked })
        }
      );
      if (!resp.ok) throw new Error('Could not save Solid preference');

      if (checked) {
        const hasGrant = await this.checkActiveAccessGrant();
        if (hasGrant) {
          this.loadWeArePostal();
        } else {
          this.setState({
            noActiveAccessGrant: true,
            rdfPostalCode: '',
            postalMismatch: false,
            loadWeAreError: ''
          });
        }
      } else {
        this.setState({
          rdfPostalCode: '',
          postalMismatch: false,
          loadWeAreError: '',
          noActiveAccessGrant: false
        });
      }
    } catch (err) {
      this.setState({ saveError: err.message });
    }
  };

  // --- Misc helpers ---
  handleResponseError(response) { return { error: true, status: response.status }; }
  handleError(error) { console.log(error.message); }

  enableEditMode = () => this.setState({ readOnlyMode: false });

  getEditButton = () => {
    const { t } = this.props;
    if (this.state.readOnlyMode) {
      return (
        <Form.Button onClick={this.enableEditMode} icon labelPosition='right' color='blue'>
          {t("Edit")}<Icon name='edit' />
        </Form.Button>
      );
    }
    return (
      <Form.Button onClick={this.updateProfile} icon labelPosition='right' color='green'>
        {t("Save")}<Icon name='save' />
      </Form.Button>
    );
  }

  getDutchColor = () => (i18n.language.startsWith("nl") ? 'blue' : 'gray');
  getFrenchColor = () => (i18n.language.startsWith("fr") ? 'blue' : 'gray');
  setFrench = () => i18n.changeLanguage('fr');
  setDutch = () => i18n.changeLanguage('nl');
  changeLanguage = () => {
    if (i18n.language.startsWith("nl")) this.setFrench();
    else if (i18n.language.startsWith("fr")) this.setDutch();
  }

  // --- WeAre Data (enkel postcode + grant melding) ---
  renderWeAreData = (t) => {
    const {
      rdfPostalCode,
      addressPostalCode,
      postalMismatch,
      loadingWeArePostal,
      loadWeAreError,
      noActiveAccessGrant
    } = this.state;

    return (
      <Segment>
        <Header>{t("WeAre Data")}</Header>
        <Divider />

        {noActiveAccessGrant && (
          <Message warning>
            <Message.Header>{t("Geen actieve toestemming")}</Message.Header>
            <p>{t("Je hebt nog geen actieve toestemming. Geef toestemming om gegevens uit je WeAre-kluis te kunnen lezen.")}</p>
            <Button color="blue" icon labelPosition="left" onClick={this.startAccessRequest}>
              <Icon name="unlock" />
              {t("Geef toestemming")}
            </Button>
          </Message>
        )}

        {loadingWeArePostal && <Loader active inline='centered' content={t("Loading…")} />}

        {loadWeAreError && !noActiveAccessGrant && <Message negative content={loadWeAreError} />}

        {!loadingWeArePostal && !loadWeAreError && !noActiveAccessGrant && (
          <>
            <p>
              <strong>{t("Postcode (WeAre)")}: </strong>
              {rdfPostalCode || '—'}
            </p>
            {postalMismatch && rdfPostalCode && (
              <div>
                <p style={{ color: '#666', marginBottom: 8 }}>
                  {t("Zipster Profiel")}: <strong>{addressPostalCode || '—'}</strong>
                </p>
                <Button
                  size="small"
                  color="orange"
                  onClick={this.applyPostalFromWeAre}
                  icon
                  labelPosition="left"
                >
                  <Icon name="arrow circle left" />
                  {t("Gebruik WeAre postcode")}
                </Button>
              </div>
            )}
          </>
        )}
      </Segment>
    );
  };

  render() {
    const { t, activeUser } = this.props;
    const { needsPostal, readOnlyMode } = this.state;

    if (!activeUser || !activeUser.uuid) {
      return (
        <Container>
          <Segment basic>
            <Message warning>
              <MessageHeader>{t('Bezig met laden')}</MessageHeader>
              <p>{t('We laden je profielgegevens. Probeer het zo meteen opnieuw als dit te lang duurt.')}</p>
            </Message>
          </Segment>
        </Container>
      );
    }

    return (
      <Container>
        <Segment basic>
          <Message>
            <MessageHeader>Info</MessageHeader>
            <p>Sommige van je gegevens, zoals je postcode, kan Zipster automatisch overnemen uit je WellData-kluis. Zo hoef je ze niet (opnieuw) in te typen. Zet hiervoor de schakelaar onder "Data" aan. </p>
            <p>Wil je Zipster daarna zelf verder verkennen? Vul de vragenlijst (links) in en je ontvangt suggesties voor ondersteuning.</p>
            <p>Let wel: Zipster is nog niet in elke regio actief, dus het ondersteuningsaanbod is momenteel enkel beschikbaar in regio's waar Zipster al werkt. Test daarom gerust met een van de volgende postcodes: 2000 Antwerpen, 2800 Mechelen, 1800 Vilvoorde, 1090 Jette of 8400 Oostende. </p>
          </Message>
        </Segment>

        <Grid columns={16} padded='horizontally' stackable>
          <Grid.Column width={12}>
            <Segment>
              <Header size='large' textAlign='center'>
                {this.state.givenName} {this.state.familyName}
              </Header>
              <Divider />

              {/* NIEUW: banner wanneer postcode ontbreekt */}
              {needsPostal && (
                <Message warning icon>
                  <Icon name="map pin" />
                  <Message.Content>
                    <Message.Header>{t("Postcode ontbreekt")}</Message.Header>
                    <p>{t("De vragenlijst werkt pas wanneer je postcode is ingevuld.")}</p>
                    <Button color="blue" onClick={this.focusPostalAndEdit} icon labelPosition="left">
                      <Icon name="edit" />
                      {t("Vul nu je postcode in")}
                    </Button>
                  </Message.Content>
                </Message>
              )}

              <Segment basic>
                <Form>
                  <Form.Group widths='equal'>
                    <Form.Input
                      fluid
                      readOnly={readOnlyMode}
                      label={t("Given Name")}
                      name='givenName'
                      value={this.state.givenName}
                      onChange={this.handleChange}
                    />
                    <Form.Input
                      fluid
                      readOnly={readOnlyMode}
                      label={t("Family Name")}
                      name='familyName'
                      value={this.state.familyName}
                      onChange={this.handleChange}
                    />
                  </Form.Group>

                  <Form.Group widths='equal'>
                    <Form.Input
                      fluid
                      type="date"
                      readOnly={readOnlyMode}
                      label={t("Date of Birth")}
                      name='birthdate'
                      value={this.state.birthdate}
                      onChange={this.handleChange}
                    />
                    <Form.Input
                      fluid
                      readOnly={readOnlyMode}
                      label={t("SSN")}
                      name='identifier'
                      value={this.state.identifier}
                      onChange={this.handleChange}
                    />
                  </Form.Group>

                  <Form.Group widths='equal'>
                    <Form.Select
                      fluid
                      readOnly={readOnlyMode}
                      label={t("Country")}
                      name='addressCountry'
                      value={this.state.addressCountry}
                      options={[
                        { key: 'be', value: 'Belgium', text: t('Belgium') },
                        { key: 'nl', value: 'Netherlands', text: t('Netherlands') }
                      ]}
                      onChange={this.handleChange}
                    />
                    <Form.Input
                      fluid
                      readOnly={readOnlyMode}
                      label={t("Postal Code")}
                      name='addressPostalCode'
                      value={this.state.addressPostalCode}
                      onChange={this.handleChange}
                      placeholder={t("Bijv. 2000")}
                      error={!readOnlyMode && needsPostal ? { content: t("Postcode is verplicht"), pointing: 'above' } : null}
                      inputRef={this.postalInputRef}
                    />
                  </Form.Group>

                  {this.getEditButton()}
                </Form>
              </Segment>
            </Segment>
          </Grid.Column>

          <Grid.Column width={4}>
            {/* Language */}
            <Segment textAlign='center'>
              <Header textAlign='center'>{t("Language")}</Header>
              <Divider />
              <Container fluid>
                <Label as={Link} onClick={this.changeLanguage} basic size='large' color={this.getDutchColor()}>Nederlands</Label>
                <Label as={Link} onClick={this.changeLanguage} basic size='large' color={this.getFrenchColor()}>Français</Label>
              </Container>
            </Segment>

            {/* Data */}
            <Segment>
              <Header>{t("Data")}</Header>
              <Divider />
              <p>{t("Control how Zipster stores your data.")}</p>
              <Checkbox
                toggle
                label={t("Save my data in my Solid pod")}
                checked={this.state.isStoreSolid}
                onChange={this.toggleIsStoreSolid}
              />
              {this.state.saveError && (
                <Message negative size="small" content={this.state.saveError} style={{ marginTop: '0.75rem' }} />
              )}
            </Segment>

            {/* WeAre Data (enkel postcode of grant-melding) */}
            {this.state.isStoreSolid && this.renderWeAreData(t)}
          </Grid.Column>
        </Grid>
      </Container>
    );
  }
}

export default withTranslation()(CitizenProfile);